import React, { useState } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';

import { useWebsite } from '../../hooks/useWebsite';
import {InputText, FormActions, CustomSelect, handleErrors } from '@/components/form'

import {
  IFunnelStageConfig,
  IReport,
  createReport
} from '../../api';

import FunnelPreview from './FunnelPreview';

const defaultStage: IFunnelStageConfig = {
  type: 'event',
  value: '',
  label: '',
  color: ''
};

const validationSchema = Yup.object().shape({
  type: Yup.string()
    .required('Type is required'),
  name: Yup.string()
    .required('Name is required')
    .min(3, 'Name must be at least 3 characters'),
  stages: Yup.array().of(
    Yup.object().shape({
      type: Yup.string()
        .oneOf(['event'], 'Type must be "event"')
        .required('Stage type is required'),
      value: Yup.string()
        .required('Event value is required'),
      label: Yup.string()
        .required('Label is required'),
      color: Yup.string()
        .required('Color is required')
        .matches(/^#[0-9A-F]{6}$/i, 'Must be a valid hex color (e.g., #FF5733)')
    })
  )
});

const stageTypes = ['event'];

interface Props {
  onSuccess: () => void;
}

const CreateReportForm: React.FC<Props>= ({ onSuccess }) => {
  const { websiteId } = useWebsite();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const defaultValues: IReport = {
    id: '',
    website: websiteId,
    type: 'funnel',
    name: '',
    stages: [{ ...defaultStage }]
  };

  return (
    <div className="card my-5">
      <div className="card-header" role="button" onClick={toggleExpand}>
        <div className='card-title align-items-start flex-column'>
          <span className="card-label fw-bold fs-3 text-primary fw-bold">
            <i className="bi bi-plus-circle text-primary me-2 fs-3"></i>
            Create new report
          </span>
        </div>
        <div className="card-toolbar">
          <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} fs-3`}></i>
        </div>
      </div>
      {isExpanded && (
        <Formik<IReport>
          initialValues={defaultValues}
          validationSchema={validationSchema}
          onSubmit={async (values: IReport, {setErrors, resetForm }) => {
            try {
              await createReport(websiteId, values);
              onSuccess();
              setIsExpanded(false);
              resetForm();
            } catch (e: unknown) {
              handleErrors(e, setErrors)
            }
          }}
        >
          {({ values, resetForm }) => (
            <Form>
              <div className='card-body px-5 py-2'>
                <Field
                  name="name"
                  type="text"
                  component={InputText}
                  label="Report Name"
                  description="Descriptive name"
                  required={true}
                />

                <div className='separator separator-dashed my-2'></div>
                <FieldArray name="stages">
                  {({ push, remove }) => (
                    <div>
                      <div className="d-flex align-items-center justify-content-center my-5">
                        <h3>Stages</h3>
                        <button
                          type="button"
                          onClick={() => push({ ...defaultStage })}
                          className="btn btn-icon btn-light-primary ms-2"
                        >
                          <i className="fa fa-plus"></i>
                        </button>
                      </div>

                      {values.stages?.map((stage, index) => (
                        <div key={index} className="stage-item mb-1">
                          <div className="d-flex align-items-center mb-3">
                            <h4 className="mb-0 me-2">Stage {index + 1}</h4>
                            { index > 0 && (
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="btn btn-icon btn-light-danger"
                                title="Remove Stage"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            )}
                          </div>

                          <div className="row g-3">
                            <div className="col-md-2">
                              <Field
                                component={CustomSelect}
                                label="Type"
                                name={`stages.${index}.type`}
                                options={stageTypes.map((t:string) => {
                                  return { value: t, label: t.toUpperCase()}
                                })}
                              />
                            </div>

                            <div className="col-md-4">
                              <Field
                                name={`stages.${index}.label`}
                                type="text"
                                component={InputText}
                                label="Label"
                                description="Display label"
                                required={true}
                              />
                            </div>

                            <div className="col-md-2">
                              <Field
                                name={`stages.${index}.color`}
                                type="text"
                                component={InputText}
                                className="text-uppercase"
                                label="Color identifier"
                                description="HTML color code"
                                placeholder="#ffffff"
                                required={true}
                              />
                            </div>

                            <div className="col-md-4">
                              <Field
                                name={`stages.${index}.value`}
                                type="text"
                                component={InputText}
                                label="Event Value"
                                description="Event identifier"
                                required={true}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </FieldArray>

                <FunnelPreview />
              </div>

              <div className="card-footer text-center">
                <Field
                  component={FormActions}
                  confirmBtnLabel="Save Report"
                  discardBtnLabel="Cancel"
                  position="end"
                  padding="pt-0"
                  close={() => {
                    resetForm();
                    setIsExpanded(false);
                  }}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}

export default CreateReportForm;
